import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  RefreshCw,
  ArrowLeft,
  Search,
  Gauge,
  Info,
  Inbox,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white text-gray-700 ' +
  'border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes ia-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
.ia-toast-in { animation: ia-toast-in .2s ease-out; }
@media (prefers-reduced-motion: reduce) { .ia-toast-in { animation: none; } }
`;

/* ------------------------------------------------------------------ */
/*  Petits composants                                                  */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value, tone = 'bg-blue-50 text-blue-600' }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-semibold leading-tight text-gray-900">{value}</div>
      <div className="truncate text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const getPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

const Pagination = ({ current, total, from, to, count, pageSize, onPageSize, onChange }) => (
  <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
      <span>
        Affichage de <span className="font-medium text-gray-700">{from}</span> à{' '}
        <span className="font-medium text-gray-700">{to}</span> sur{' '}
        <span className="font-medium text-gray-700">{count}</span>
      </span>
      <label className="flex items-center gap-2">
        Par page
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          {[10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </div>
    {total > 1 && (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Page précédente"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPages(current, total).map((page, i) =>
          page === '…' ? (
            <span key={`dots-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onChange(page)}
              aria-current={current === page ? 'page' : undefined}
              className={`h-9 min-w-[2.25rem] rounded-lg border px-3 text-sm font-medium transition-colors ${
                current === page
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Page suivante"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const IndicateurAssociation = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [indicateurs, setIndicateurs] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [typePersonnel, setTypePersonnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [togglingId, setTogglingId] = useState(null);
  const [toasts, setToasts] = useState([]);

  const needsFiliale = !filialeId || isConsolidated;

  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  /* ---------- Chargement ---------- */
  const fetchIndicateurs = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/indicateurs');
      setIndicateurs(response.data || []);
    } catch (error) {
      console.error('Erreur récupération indicateurs:', error);
      setIndicateurs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociations = async () => {
    try {
      let endpoint = `/api/indicateurs/type/${typeId}?withPond=true`;
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `&filiale_id=${filialeId}`;
      }
      const response = await http.get(endpoint);
      setAssociations(response.data || []);
    } catch (error) {
      console.error('Erreur récupération associations:', error);
      setAssociations([]);
    }
  };

  const fetchTypePersonnel = async () => {
    try {
      const response = await http.get(`/api/types-personnel/${typeId}`);
      setTypePersonnel(response.data);
    } catch (error) {
      console.error('Erreur récupération type personnel:', error);
    }
  };

  useEffect(() => {
    fetchIndicateurs();
    fetchAssociations();
    fetchTypePersonnel();
  }, [typeId]);

  // Les associations dépendent de la filiale : on les recharge si elle change
  useEffect(() => {
    fetchAssociations();
  }, [filialeId, isConsolidated]);

  /* ---------- Actions ---------- */
  const handleToggle = async (indicateurId) => {
    if (togglingId !== null) return;
    if (!filialeId || isConsolidated) {
      notify('warning', 'Sélectionnez une filiale spécifique pour gérer les associations.');
      return;
    }
    try {
      setTogglingId(indicateurId);
      await http.post(`/api/indicateurs/toggle/${typeId}/${indicateurId}`, {
        filiale_id: filialeId
      });
      await fetchAssociations();
    } catch (error) {
      console.error('Erreur toggle:', error);
      notify('error', "Impossible de modifier l'indicateur. Réessayez.");
    } finally {
      setTogglingId(null);
    }
  };

  const isAssociated = (indicateurId) => {
    return associations.some((assoc) => assoc.idindicateur === indicateurId && assoc.etat === 1);
  };

  /* ---------- Filtrage + pagination ---------- */
  const filteredIndicateurs = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return indicateurs.filter((ind) => {
      const matchSearch =
        !searchTerm ||
        ind.Libelle_indicateur?.toLowerCase().includes(term) ||
        ind.nom_variable?.toLowerCase().includes(term) ||
        ind.idindicateur?.toString().includes(searchTerm);
      if (!matchSearch) return false;
      if (statusFilter === 'active') return isAssociated(ind.idindicateur);
      if (statusFilter === 'inactive') return !isAssociated(ind.idindicateur);
      return true;
    });
  }, [indicateurs, associations, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredIndicateurs.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfLast = safePage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentItems = filteredIndicateurs.slice(indexOfFirst, indexOfLast);

  /* ---------- Stats ---------- */
  const nbActifs = indicateurs.filter((i) => isAssociated(i.idindicateur)).length;
  const nbInactifs = indicateurs.length - nbActifs;
  const pctActifs = indicateurs.length ? Math.round((nbActifs / indicateurs.length) * 100) : 0;

  const tabs = [
    { id: 'all', label: 'Tous', count: indicateurs.length },
    { id: 'active', label: 'Activés', count: nbActifs },
    { id: 'inactive', label: 'Désactivés', count: nbInactifs }
  ];

  /* ---------- Rendu ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{keyframes}</style>

      <BlueHeader
        title={`Indicateurs : ${typePersonnel?.libelle || 'Type Personnel'}`}
        subtitle="Activer ou désactiver les indicateurs pour ce type de personnel"
        actions={[
          {
            label: 'Retour',
            icon: ArrowLeft,
            onClick: () => navigate('/indicateurs'),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: () => {
              fetchIndicateurs();
              fetchAssociations();
            },
            primary: false
          }
        ]}
      />

      <div className="space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {/* Bandeau contexte filiale */}
        {needsFiliale && (
          <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <p>
              Vous consultez la vue consolidée. Choisissez une filiale dans le sélecteur pour activer ou désactiver
              des indicateurs.
            </p>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Gauge} label="Indicateurs disponibles" value={loading ? '–' : indicateurs.length} />
          <StatCard
            icon={Check}
            label="Activés pour ce type"
            value={loading ? '–' : nbActifs}
            tone="bg-green-50 text-green-600"
          />
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-gray-500">Taux d'activation</span>
              <span className="text-2xl font-semibold text-gray-900">{loading ? '–' : `${pctActifs}%`}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                style={{ width: `${loading ? 0 : pctActifs}%` }}
              />
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Liste des indicateurs</h3>
              <p className="mt-0.5 text-sm text-gray-500">Cliquez sur une ligne pour activer ou désactiver un indicateur</p>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un indicateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-9 text-sm placeholder-gray-400 transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 lg:w-80"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filtres d'état */}
          <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-6">
            <div role="tablist" className="inline-flex rounded-xl bg-gray-100 p-1">
              {tabs.map((tab) => {
                const active = statusFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setStatusFilter(tab.id)}
                    className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all ${
                      active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        active ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-1/5 animate-pulse rounded bg-gray-100" />
                  </div>
                  <div className="h-8 w-24 animate-pulse rounded-full bg-gray-100" />
                </div>
              ))}
            </div>
          ) : filteredIndicateurs.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                {searchTerm || statusFilter !== 'all' ? <Search className="h-7 w-7" /> : <Inbox className="h-7 w-7" />}
              </div>
              <h4 className="text-base font-semibold text-gray-800">
                {searchTerm || statusFilter !== 'all' ? 'Aucun indicateur trouvé' : 'Aucun indicateur disponible'}
              </h4>
              <p className="mt-1 max-w-sm text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Modifiez votre recherche ou le filtre pour voir d’autres indicateurs.'
                  : 'Créez d’abord des indicateurs depuis la page Indicateurs.'}
              </p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className={`${secondaryBtn} mt-5`}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {currentItems.map((indicateur) => {
                const associated = isAssociated(indicateur.idindicateur);
                const assoc = associations.find((a) => a.idindicateur === indicateur.idindicateur);
                const busy = togglingId === indicateur.idindicateur;
                return (
                  <li
                    key={indicateur.idindicateur}
                    onClick={() => handleToggle(indicateur.idindicateur)}
                    className={`flex cursor-pointer items-center justify-between gap-4 px-4 py-4 transition-colors sm:px-6 ${
                      associated ? 'hover:bg-green-50/40' : 'hover:bg-blue-50/40'
                    } ${busy ? 'opacity-70' : ''}`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                          associated ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        <Gauge className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900">{indicateur.Libelle_indicateur}</div>
                        <code className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">
                          {indicateur.nom_variable}
                        </code>
                        {associated && (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <span className="rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700">
                              Coef : {assoc?.coeficient || 1.0}
                            </span>
                            {assoc?.plafond && (
                              <span className="rounded-md bg-amber-50 px-2 py-1 font-medium text-amber-700">
                                Plafond : {assoc.plafond}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      {associated ? (
                        <span className="hidden items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-700 sm:inline-flex">
                          <Check className="h-4 w-4" />
                          Activé
                        </span>
                      ) : (
                        <span className="hidden items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 sm:inline-flex">
                          <X className="h-4 w-4" />
                          Désactivé
                        </span>
                      )}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={associated}
                        aria-label={`${associated ? 'Désactiver' : 'Activer'} ${indicateur.Libelle_indicateur}`}
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(indicateur.idindicateur);
                        }}
                        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ${
                          associated ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition-transform ${
                            associated ? 'translate-x-[22px]' : 'translate-x-0.5'
                          }`}
                        >
                          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-500" />}
                        </span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && filteredIndicateurs.length > 0 && (
            <Pagination
              current={safePage}
              total={totalPages}
              from={indexOfFirst + 1}
              to={Math.min(indexOfLast, filteredIndicateurs.length)}
              count={filteredIndicateurs.length}
              pageSize={pageSize}
              onPageSize={setPageSize}
              onChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* Notifications */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const styles = {
            success: { box: 'border-green-200', icon: CheckCircle2, color: 'text-green-600' },
            error: { box: 'border-red-200', icon: AlertCircle, color: 'text-red-600' },
            warning: { box: 'border-amber-200', icon: AlertTriangle, color: 'text-amber-600' }
          }[t.type];
          const Icon = styles.icon;
          return (
            <div
              key={t.id}
              className={`ia-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg ${styles.box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles.color}`} />
              <p className="flex-1 text-sm text-gray-800">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndicateurAssociation;